import { useEffect, useRef, useState } from "react";

import "react-toastify/dist/ReactToastify.css";
import api from "../../../services/api";
import { TablePage } from "../../../containers";
import moment from "../../../utils/momentConfig";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faSearch } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import toastOptions from "../../../utils/toastOptions";
import { utils } from "../../../utils/utils";
import { EcclesiasticalFunction } from "../../../models/enums/EcclesiasticalFunction";
import CongregationSelect from "../../../components/congregation-select";

export default function MemberAbsenceList() {
  const [refresh, setRefresh] = useState<boolean>();
  const [total, setTotal] = useState(0);
  const [totalMeetings, setTotalMeetings] = useState(-1);
  const [totalLabores, setTotalLabores] = useState(-1);
  const [pageNo, setPageNo] = useState<number>(1);
  const [years, setYears] = useState([] as number[]);
  const initialYear = useRef<number>(2022);
  const currentYear = useRef<number>(moment().year());
  const [termSearch, setTermSearch] = useState<string>("");
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [isReporting, setIsReporting] = useState<boolean>();
  const [idCongregation, setIdCongregation] = useState<number>(1);

  const columns = [
    {
      name: "Membro",
      sortable: true,
      grow: 2,
      cell: (row: any) => <div className="uk-flex uk-flex-column">
        <span>{row.name}</span>
        <span className="uk-text-italic">({row.congregation})</span>
      </div>
    },
    {
      name: "Detalhes",
      sortable: true,
      cell: (row: any) => <span className="uk-text-muted uk-text-bold">{row.absenceCount} faltas</span>
    },
    {
      name: 'Ações',
      button: true,
      sortable: false,
      cell: (row: any) => (<div className='uk-flex'>
        <Link to={`/membros/${row.id}`}
          uk-tooltip="Ver detalhes do membro"
          target="_blank"
        >
          <FontAwesomeIcon icon={faSearch} />
        </Link>      
      </div>)
    }
  ];

  useEffect(() => {
    handleYears();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm, idCongregation]);

  useEffect(() => {
    fetchTotal();
  }, [currentYear.current, idCongregation]);

  const handleYears = () => {
      setYears([]);
  
    let initYear = currentYear.current;

    if (initYear === moment().year() && moment().month() < 2) {
      initYear = initYear - 1;
      currentYear.current = initYear;
    }

    for (let i = initYear; i >= initialYear.current; i--) {
      setYears(array => [...array, i]);
    }
  }  

  const fetch = async (page: number, limit: number) => {
    try {
      setTotal(0);
      
      if (currentYear.current === moment().year() && moment().month() < 2) {
        currentYear.current -= 1;
      }
      
      let abParams = {
        page: page,
        limit: limit,
        year: currentYear.current,
        idCongregation: idCongregation
      } as any;
      
      if (termSearch) {
        abParams = { ...abParams, name: termSearch?.toUpperCase() };
      }
      
      const response = await api.get(`members/absences`, {
        params: abParams
      });

      setTotal(response.data.total);

      setPageNo(page);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
      return { data: [], total: 0 };
    }
  };

  const fetchTotal = async () => {
    try {
      setTotalMeetings(-1);
      setTotalLabores(-1);

      const { data: dataMeeting } = await api.get(`meetings`, {
        params: {
          page: 0,
          limit: 0,
          year: currentYear.current,
          isOficial: true
        }
      });

      const { data: dataLabores } = await api.get(`members`, {
        params: {
          page: 0,
          limit: 0,
          function: EcclesiasticalFunction.LABORERS,
          congregation: idCongregation
        }
      });
      
      setTotalMeetings(dataMeeting.total);

      setTotalLabores(dataLabores.total);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
      setTotalMeetings(0);
      setTotalLabores(0);
    }
  }

  const handleSearchByName = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  const handleReport = () => async () => {
    try {
      setIsReporting(true);
      const { data } = await api.post(`members/absences/report`, {
        year: currentYear.current,
        name: termSearch?.toUpperCase(),
        idCongregation: idCongregation
      });

      const nameLetter = `relatório-ausências-${currentYear.current}-${new Date().getTime()}.pdf`;
      
      utils.printPDF(nameLetter, data.item);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsReporting(false);
    }
  };

  const renderOptions = () => {
    return <div>
      <div className="uk-width-1-1@m uk-flex uk-flex-column uk-margin-small-bottom">
        <span className="uk-text-bold uk-text-small">
          {totalMeetings < 0 ? "Carregando total de reuniões..." : `Tivemos ${totalMeetings} reuniões em ${currentYear.current} e ${total} obreiros faltaram em pelo menos 3 delas`}
          {(totalLabores > 0) && `, dentre um total de ${totalLabores} obreiros.`}
        </span>
      </div>
      <div className="uk-grid-small" uk-grid="true">
        <div className="uk-width-1-6@m uk-flex uk-flex-middle">
          <label htmlFor="year" className="uk-margin-small-right">Ano</label>
          <div className="uk-form-controls">
            <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
              <select className="uk-form-small"
                id="year" 
                placeholder="Ano"
                name="year"
                value={currentYear.current}
                onChange={(ev) => { currentYear.current = +(ev.target.value); setRefresh(!refresh); }}
              >  
                {
                  years?.map(y => {
                    return <option key={`ano${y}`} value={`${y}`}>{y}</option>
                  })
                }
              </select>
              <button 
                className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                style={{textTransform: 'none'}} 
                type="button" 
                tabIndex={-1}
              >
                <span></span>
                <span uk-icon="icon: chevron-down"></span>
              </button>
            </div>
          </div>
        </div>
        <div className="uk-search uk-search-default uk-width-1-3@m">
          <span
            className="uk-search-icon-flip"
            uk-search-icon="true"
          ></span>
          <input
            className="uk-search-input uk-input uk-form-small"
            type="search"
            placeholder="Pesquise por nome..."
            style={{ textTransform: "uppercase" }}
            onChange={(ev) => {
              handleSearchByName(ev.target.value);
            }}
          />
        </div>
        <div className="uk-width-1-3@m uk-flex uk-flex-center">
          <CongregationSelect afterSelect={(id: number)=> setIdCongregation(id)}/>
        </div>
      </div>
    </div>
  }

  return (<>
    <TablePage 
      title="Ausências por Reunião"
      subTitle={`Membros com 3 ou mais ausências por reunião no ano de ${currentYear.current}`}
      columns={columns}
      fetch={fetch}
      refresh={refresh}
      options={renderOptions()}
      backUrl={`/secretaria`}
      messageEmpty={<span>Nenhum <b>membro</b> encontrado!</span>}
      pageNo={pageNo}
      extraHeaderButtons={<>
        <button className="uk-button uk-button-small uk-button-default"
          disabled={isReporting || total === 0}
          onClick={handleReport()}
        >
          <span className="uk-margin-small-right">{isReporting ? "Imprimindo..." : "Imprimir"}</span>
          {isReporting ? <div uk-spinner="true; ratio:0.5"></div> : <FontAwesomeIcon icon={faPrint} />}
        </button>
      </>}
    />
  </>);
}